import React from "react";
import { render, findDOMNode } from "react-dom";
import classNames from "classnames/bind";
import { AbstractTrackURL, IntroHeadline, IntroParagraph } from "./components";
import { OverallGraphicRep } from "./rep_graphics.js";
import { PuzzleLegend } from "./puzzle.js";
import { Nav, MobileNav, links } from "./nav.js";
import { expandBasicData, PRODUCT_KEYS as S } from "./data_helpers.js";

let appData = (window.dbgAppData = $("#app_data").data("data"));
expandBasicData(appData);

const L = appData.survey.labels;

class TrackURL extends AbstractTrackURL {
  static basePathname = window.location.pathname;

  state = {};

  updateAppState(urlParts, isInitialize) {
    let { fragParams } = urlParts;
    let { tour } = fragParams;
    if (tour || !isInitialize) {
      if (window.impact_tours) window.impact_tours.setToken(tour || "");
    }
  }

  generateUrlParts() {
    let { toursToken } = this.state;
    let fragParams = {};
    if (toursToken) {
      fragParams["tour"] = toursToken;
    }
    return { fragParams };
  }
}

if (appData.survey.product_key === S.ECE) {
  // HACK: stub out `TrackURL` — report_intro_ece has its own!
  TrackURL = function () {
    return null;
  };
  TrackURL.intializeState = function () {};
}

const LandingIntro = ({
  links,
  survey,
  target,
  landing_summary,
  overall_organization_statement,
}) => (
  <div className="landing-intro">
    <div>
      <IntroHeadline {...{ target, survey, overall_organization_statement }} />
      <IntroParagraph
        {...{ target, landing_summary, overall_product: L.overall_product }}
      />
    </div>
    <p className="about-link">
      <a href={links.about_report}>About the Survey</a>
    </p>
  </div>
);

const IntroQuestions = ({ links, target }) => (
  <div className="intro-questions">
    <h2 className="landing-question">
      <a href={links.five_essentials}>
        <h5>{L.overall_product_long}</h5>
        How is {target.short_name} performing on each of the{" "}
        {L.overall_product_short}?
      </a>
    </h2>
    <h2 className="landing-question">
      <a href={links.all_measures}>
        <h5>All Measures</h5>
        How is {target.short_name} performing across all measures?
      </a>
    </h2>
  </div>
);

function _ff(val, n = 1) {
  // fill-in for Django template |floatformat, mostly for null handling
  return val == null ? "" : val.toFixed(n);
}

const ResponseRates = ({
  target,
  survey_settings,
  school_stats,
  school_tables,
}) => (
  <div className="response-rates">
    <h2>Survey Response Rates for {target.name}</h2>
    {target.is_group ? (
      <div>
        <p>
          {school_stats.num_reporting} of {school_stats.num_total} schools (
          {_ff(school_stats.pct_reporting)}%) provided enough survey data to
          receive a report. The district-wide response rate is{" "}
          {_ff(survey_settings.district_response_rate)}%.
        </p>

        {school_tables.map(([schools, header, empty_note], idx) => (
          <table key={idx} className="table no-select">
            <thead>
              <tr>
                <th>{header}</th>
                {survey_settings.includes_students ? (
                  <th>Student Rate</th>
                ) : null}
                {survey_settings.includes_teachers ? (
                  <th>Teacher Rate</th>
                ) : null}
                {survey_settings.includes_parents ? <th>Parent Rate</th> : null}
              </tr>
            </thead>
            <tbody>
              {schools.map((school) => (
                <tr key={school.slug}>
                  <td>
                    <a
                      href={links.report_intro_template.replace(
                        "_PLACEHOLDER_",
                        school.slug,
                      )}
                    >
                      {school.name}
                    </a>
                  </td>
                  {survey_settings.includes_students ? (
                    <td>{_ff(school.student_response_rate)}%</td>
                  ) : null}
                  {survey_settings.includes_teachers ? (
                    <td>{_ff(school.teacher_response_rate)}%</td>
                  ) : null}
                  {survey_settings.includes_parents ? (
                    <td>{_ff(school.parent_response_rate)}%</td>
                  ) : null}
                </tr>
              ))}
              {!schools.length ? (
                <tr>
                  {/* NOTE: colSpan may be too large but should be harmless unless table-layout is "fixed" */}
                  <td className="empty" colSpan="4">
                    {empty_note}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        ))}
      </div>
    ) : (
      <div>
        <table className="table no-select">
          <thead>
            <tr>
              <th>Respondent</th>
              <th>Response Rate</th>
              <th>({survey_settings.org_abbrev})</th>
            </tr>
          </thead>
          <tbody>
            {survey_settings.includes_students ? (
              <tr>
                <td>Students</td>
                <td>
                  <span
                    className={
                      target.student_response_rate < 50.0
                        ? "low_response_rate"
                        : null
                    }
                  >
                    {_ff(target.student_response_rate)}%
                  </span>
                </td>
                <td>({_ff(survey_settings.student_response_rate)}%)</td>
              </tr>
            ) : (
              <tr className="disable-row">
                <td colSpan="3">Students not surveyed</td>
              </tr>
            )}
            {survey_settings.includes_teachers ? (
              <tr>
                <td>Teachers</td>
                <td>
                  <span
                    className={
                      target.teacher_response_rate < 50.0
                        ? "low_response_rate"
                        : null
                    }
                  >
                    {_ff(target.teacher_response_rate)}%
                  </span>
                </td>
                <td>({_ff(survey_settings.teacher_response_rate)}%)</td>
              </tr>
            ) : (
              <tr className="disable-row">
                <td colSpan="3">Teachers not surveyed</td>
              </tr>
            )}
            {survey_settings.includes_parents ? (
              <tr>
                <td>Parents</td>
                <td>
                  <span
                    className={
                      target.parent_response_rate < 50.0
                        ? "low_response_rate"
                        : null
                    }
                  >
                    {_ff(target.parent_response_rate)}%
                  </span>
                </td>
                <td>({_ff(survey_settings.parent_response_rate)}%)</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

class MobileIntroContent extends React.PureComponent {
  state = {};

  render() {
    let {
      survey,
      target,
      essentials,
      allRanks,
      school_stats,
      school_tables,
      survey_settings,
      landing_summary,
      overall_organization_statement,
    } = this.props;
    let { detailsOpen } = this.state;

    const setDetailsOpen = (detailsOpen) => {
      this.setState({ detailsOpen });

      // HACK: the ECE stuff ended up needing this to suppress crayon box
      let _setParentState = findDOMNode(this)._setDetailsOpen;
      if (_setParentState) _setParentState(detailsOpen);
    };

    return (
      <div className="mobile-intro-content">
        {survey.product_key === S._5E ? (
          <div className="graphic-rep-wrapper">
            <OverallGraphicRep
              survey={survey}
              essentials={essentials}
              generateLink={(d) =>
                links.essential_template.replace("_PLACEHOLDER_", d.slug)
              }
            />
            <PuzzleLegend allRanks={allRanks} />
          </div>
        ) : null}

        <IntroHeadline
          {...{ target, survey, overall_organization_statement }}
        />
        <IntroQuestions {...{ links, target }} />
        <div className="more-info">
          <a
            className="details-toggle"
            href="#" // (needed for consistent styling)
            onClick={(evt) => {
              evt.preventDefault();
              setDetailsOpen(true);
            }}
          >
            Survey Response Rates
          </a>
          <a href={links.summary}>Dashboard Summary</a>
        </div>

        <div className={classNames("details-popup", { open: detailsOpen })}>
          <a
            className="details-toggle"
            onClick={(evt) => {
              setDetailsOpen(false);
            }}
          >
            <span className="glyphicon glyphicon-remove" />
          </a>
          <LandingIntro
            {...{
              links,
              survey,
              target,
              landing_summary,
              overall_organization_statement,
            }}
          />
          <ResponseRates
            {...{ target, survey_settings, school_stats, school_tables }}
          />
        </div>
      </div>
    );
  }
}

const ReportIntro = ({
  survey,
  target,
  essentials,
  allRanks,
  school_stats,
  school_tables,
  survey_settings,
  landing_summary,
  overall_organization_statement,
}) => (
  <section id="report-body">
    <div id="react_intro_target"></div>
    <div className="grid desktop-intro-content">
      <div className="column landing-puzzle-col">
        <div id="react_puzzle_target">
          <OverallGraphicRep
            survey={survey}
            essentials={essentials}
            generateLink={(d) =>
              links.essential_template.replace("_PLACEHOLDER_", d.slug)
            }
          />
        </div>
        <PuzzleLegend allRanks={allRanks} />
      </div>
      <div className="column landing-intro-col">
        <LandingIntro
          {...{
            links,
            survey,
            target,
            landing_summary,
            overall_organization_statement,
          }}
        />
        <IntroQuestions {...{ links, target }} />
        <ResponseRates
          {...{ target, survey_settings, school_stats, school_tables }}
        />
      </div>
      <div className="column col-narrow" />
    </div>
  </section>
);

render(
  <MobileNav {...appData} />,
  document.querySelector("#react_mobile_header"),
);

TrackURL.intializeState(true);
render(
  <div>
    <Nav {...appData} overall={null} />
    <ReportIntro {...appData} />
    <MobileIntroContent {...appData} />
    <TrackURL
      ref={
        window.impact_tours
          ? (tracker) => {
              window.impact_tours.watchToken((token) => {
                if (tracker) tracker.setState({ toursToken: token });
              });
            }
          : null
      }
    />
  </div>,
  document.querySelector("#react_target"),
);

// ~HACK: expose appData so report_intro_ece can skip recreating it
document.querySelector("#react_intro_target")._inq_appData = appData;
